import React, { useState, useEffect, useCallback, useRef } from 'react';
import Select, { type SingleValue } from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import VacationsService from '@services/VacationsService';
import CorgeeStorageHelper from '@components/Globals/CorgeeStorageHelper';
import {
  checkHRApproverPermissions,
  checkPTOApproverPermissions,
} from '@/components/utils/CorgeeHelper';
import { useSocketConnectionId } from '@/contexts/SocketContext';

import './VacationsRequestPage.css';
import moment from 'moment';
import { Modal } from '@/components/utils/Modal';
import { buildReportSelectStyles } from '@/components/Reports/reportSelectStyles';

interface VrDateInputProps {
  value?: string;
  onClick?: () => void;
  onClear: () => void;
  placeholder?: string;
}
const VrDateInput = React.forwardRef<HTMLInputElement, VrDateInputProps>(
  ({ value, onClick, onClear, placeholder }, ref) => (
    <div className="vr-date-input-wrapper">
      <input
        ref={ref}
        type="text"
        value={value || ''}
        onClick={onClick}
        readOnly
        className="vr-date-input"
        placeholder={placeholder || 'MM/DD/YYYY'}
        onChange={() => {}}
      />
      <button
        type="button"
        className="vr-date-icon-btn"
        onClick={value ? onClear : onClick}
        tabIndex={-1}
      >
        <i className="fa fa-calendar" style={{ fontSize: 12 }} />
      </button>
    </div>
  ),
);
VrDateInput.displayName = 'VrDateInput';

interface Permission {
  Id: number;
  Name: string;
  Days: number;
  AvailableEndDate?: boolean;
  IsWorkingDay?: boolean;
  CanSelectPastDates?: boolean;
  Message?: string;
}

interface RequestTypeOption {
  value: number;
  label: string;
  id: number | null;
  Message?: string;
}

interface Person {
  PersonID: number;
  Name: string;
  TeamId?: number;
  Team?: string;
  PTOApprover?: boolean;
}

interface PersonDetails extends Person {
  TeamId?: number;
  Team?: string;
}

interface Holiday {
  Date: string;
}

interface VacationTableItem {
  creationDate: string;
  LeadId: number;
  LeadName: string;
  requestTypeName: string;
  requestTypeId: number;
  days: number;
  dateFrom: string;
  dateTo: string;
  workedOn: string;
  isWorkingDay: boolean;
  status: string;
  requesterComments: string;
  approverComments: string;
  requestId: number;
  enabled: boolean;
}

interface PTODaysLeft {
  maternityLeaveLeft: number;
  bereavementLeaveLeft: number;
  absenceLeft: number;
  sickLeaveLeft: number;
  paternityLeaveLeft: number;
  birthdayHalfLeaveLeft: number;
  specialSwitchLeaveLeft: number;
}

type SortColumn =
  | 'default'
  | 'creationDate'
  | 'lead'
  | 'requestType'
  | 'days'
  | 'workedOn'
  | 'dateFrom'
  | 'dateTo'
  | 'status'
  | 'requesterComments'
  | 'approverComments';

type SortDirection = 'asc' | 'desc' | '';

const ALLOWED_CURRENT_DAY_PERMISSIONS = [
  'Absence',
  'Sick Leave',
  'Day Off',
  'Appointment',
  'Appointments',
  'Maternity Leave',
  'Bereavement Leave',
  'Paternity Leave',
] as const;

function parseDate(str: string): Date
{
  const [y, m, d] = str.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
}

/**
 * Normalizes an API date to local midnight. `GetHireDate` returns `MM/DD/YYYY` while other
 * endpoints return `YYYY-MM-DD[THH:mm:ss]`, so both are accepted. Parsing explicitly avoids
 * `new Date(str)`, which reads date-only ISO strings as UTC and shifts the day in negative
 * offsets, landing day arithmetic on the wrong side of the anniversary.
 */
function parseApiDate(str: string): Date
{
  const datePart = str.split('T')[0]!.trim();
  return datePart.includes('/') ? parseMDYDate(datePart) : parseDate(datePart);
}

/** Formats a local Date as `YYYY-MM-DD` without the UTC shift of `toISOString()`. */
function toLocalISODate(date: Date): string
{
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${m}-${d}`;
}

/**
 * Completed years of service, counted by calendar anniversary.
 * Dividing a day count by 365 drifts with leap years and cannot land exactly on the
 * renewal date, so the anniversary is compared directly instead.
 */
function calcYearsOfService(hireDate: string, reference: Date): number
{
  const hire = parseApiDate(hireDate);
  // An unparseable hire date must not fall through the reform tables as NaN, which would
  // skip every threshold and report the maximum allowance.
  if (Number.isNaN(hire.getTime())) return 0;

  const ref = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
  const anniversary = new Date(ref.getFullYear(), hire.getMonth(), hire.getDate());
  let years = ref.getFullYear() - hire.getFullYear();
  if (ref < anniversary) years--;
  return Math.max(0, years);
}

function strDateToMMDDYYYY(str: string): string
{
  const [y, m, d] = str.split('-') as [string, string, string];
  return `${m}/${d}/${y}`;
}

function getDateWithAddedDay(strDate: string): string
{
  const date = new Date(strDate);
  date.setDate(date.getDate() + 2);
  return date.toISOString().split('T')[0]!;
}

function calcDaysBetweenDates(startDate: Date | number, endDate: Date | number): number
{
  return Math.round((+endDate - +startDate) / (1000 * 60 * 60 * 24));
}

function getRequestStatus(status: number): string
{
  switch (status)
  {
    case 0:
      return 'Requested';
    case 1:
      return 'Approved';
    default:
      return 'Rejected';
  }
}

function parseMDYDate(str: string): Date
{
  const [m, d, y] = str.split('/').map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
}

function getVacationDaysNewReform(years: number): number
{
  switch (true)
  {
    case years < 1:
      return 0;
    case years < 6:
      return 10 + 2 * years;
    case years < 36:
      return 20 + 2 * Math.floor(years * 0.195);
    default:
      return 32;
  }
}

function getVacationDaysOldReform(years: number): number
{
  switch (true)
  {
    case years < 1:
      return 6;
    case years < 6:
      return 4 + 2 * years;
    case years < 34:
      return 12 + 2 * Math.floor(years * 0.2);
    default:
      return 24;
  }
}

function calcVacationsPerYear(hireDate: string, yearsHired: number): number
{
  const hiredAfter2023 = parseApiDate(hireDate) >= new Date(2023, 0, 1);
  const currentYear = new Date().getFullYear();
  const dateForRenewal = new Date(parseApiDate(hireDate).setFullYear(2023));
  const isRenewed = dateForRenewal <= new Date();

  return hiredAfter2023 || isRenewed || currentYear > 2023
    ? getVacationDaysNewReform(yearsHired)
    : getVacationDaysOldReform(yearsHired);
}

/** Vacation days for the previous (0), current (1) or next (2) vacation period. */
function calcVacationsPerYearOffset(hireDate: string, yearsHired: number, offset: 0 | 1 | 2): number
{
  const years = offset === 0 ? yearsHired - 1 : offset === 2 ? yearsHired + 1 : yearsHired;
  return calcVacationsPerYear(hireDate, Math.max(0, years));
}

function sortTableItems(
  items: VacationTableItem[],
  column: SortColumn,
  direction: SortDirection,
  originalItems: VacationTableItem[],
): VacationTableItem[]
{
  if (column === 'default') return originalItems;

  const sorted = [...items];
  const dir = direction === 'asc' ? 1 : -1;

  const dateSorter = (key: keyof VacationTableItem) =>
    sorted.sort((a, b) =>
    {
      const aVal = a[key] as string;
      const bVal = b[key] as string;
      if (!aVal) return 1;
      if (!bVal) return -1;
      return (parseMDYDate(aVal).getTime() - parseMDYDate(bVal).getTime()) * dir;
    });

  const strSorter = (key: keyof VacationTableItem) =>
    sorted.sort((a, b) => (a[key] as string).localeCompare(b[key] as string) * dir);

  const numSorter = (key: keyof VacationTableItem) =>
    sorted.sort((a, b) => ((a[key] as number) - (b[key] as number)) * dir);

  switch (column)
  {
    case 'creationDate':
      return dateSorter('creationDate');
    case 'lead':
      return strSorter('LeadName');
    case 'requestType':
      return strSorter('requestTypeName');
    case 'days':
      return numSorter('days');
    case 'workedOn':
      return dateSorter('workedOn');
    case 'dateFrom':
      return dateSorter('dateFrom');
    case 'dateTo':
      return dateSorter('dateTo');
    case 'status':
      return strSorter('status');
    case 'requesterComments':
      return strSorter('requesterComments');
    case 'approverComments':
      return strSorter('approverComments');
    default:
      return originalItems;
  }
}

interface SortIconProps {
  column: SortColumn;
  sortedBy: SortColumn;
  direction: SortDirection;
}

const SortIcon: React.FC<SortIconProps> = ({ column, sortedBy, direction }) => (
  <i
    className={
      sortedBy === column
        ? direction === 'asc'
          ? 'fa fa-sort-asc'
          : 'fa fa-sort-desc'
        : 'fa fa-sort'
    }
    style={{ display: 'inline-block', width: 20 }}
  />
);

interface SortableThProps {
  label: string;
  column: SortColumn;
  sortedBy: SortColumn;
  direction: SortDirection;
  onSort: (col: SortColumn) => void;
  title?: string;
}

const SortableTh: React.FC<SortableThProps> = ({
  label,
  column,
  sortedBy,
  direction,
  onSort,
  title,
}) => (
  <th className="enable-help-tooltip" data-title={title ?? label} onClick={() => onSort(column)}>
    <span className="headerIconPadding">{label}</span>
    <SortIcon column={column} sortedBy={sortedBy} direction={direction} />
  </th>
);

const VacationsRequestPage: React.FC = () =>
{
  const isDark = document.documentElement.classList.contains('dark');
  const personIdInit = CorgeeStorageHelper.personId() as number;
  const isAdminInit = CorgeeStorageHelper.isAdmin() as boolean;
  const today = new Date().toISOString().split('T')[0]!;

  // ── State ──────────────────────────────────────────────────────────────────
  const [itemsRequestTypes, setItemsRequestTypes] = useState<RequestTypeOption[]>([]);
  const [itemsPermissions, setItemsPermissions] = useState<Permission[]>([]);
  const [selectedPermission, setSelectedPermission] = useState<Permission | null>(null);
  const [availableEndDate, setAvailableEndDate] = useState(true);
  const [itemsAssigned, setItemsAssigned] = useState<Person[]>([]);
  const [itemsAssignedOnTeam, setItemsAssignedOnTeam] = useState<Person[]>([]);
  const [selectedRequestType, setSelectedRequestType] = useState<RequestTypeOption | null>(null);
  const [selectedAssigned, setSelectedAssigned] = useState<Person | null>(null);
  const [selectedCurrentPerson, setSelectedCurrentPerson] = useState<Person | null>(null);
  const [personId, setPersonId] = useState<number>(personIdInit);
  const [originalPersonId] = useState<number>(personIdInit);
  const [personDetails, setPersonDetails] = useState<PersonDetails | null>(null);
  const [selectedDateFrom, setSelectedDateFrom] = useState(today);
  const [selectedDateTo, setSelectedDateTo] = useState(today);
  const [selectedDateSwitchOff, setSelectedDateSwitchOff] = useState(today);
  const [renewalDate, setRenewalDate] = useState<string | null>(null);
  const [nextRenewalDate, setNextRenewalDate] = useState<string | null>(null);
  const [itemsHolidays, setItemsHolidays] = useState<Holiday[]>([]);
  // Latest holidays for async chains to avoid stale-closure reads
  const itemsHolidaysRef = useRef<Holiday[]>([]);
  const [selectedDateFromMsg, setSelectedDateFromMsg] = useState<string | null>(null);
  const [selectedDateToMsg, setSelectedDateToMsg] = useState<string | null>(null);
  const [commentsValue, setCommentsValue] = useState('');
  const [invalidRequestType, setInvalidRequestType] = useState(false);
  const [invalidAssigned, setInvalidAssigned] = useState(false);
  const [vacationsWarning, setVacationsWarning] = useState<string | null>(null);
  const [switchOffWarning, setSwitchOffWarning] = useState<string | null>(null);
  const [isLoadingRequests, setIsLoadingRequests] = useState(true);
  const [selectedRequestItem, setSelectedRequestItem] = useState<VacationTableItem | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [originalItems, setOriginalItems] = useState<VacationTableItem[]>([]);
  const [itemsFillTable, setItemsFillTable] = useState<VacationTableItem[]>([]);
  const [prevItemsFillTable, setPrevItemsFillTable] = useState<VacationTableItem[]>([]);
  const [tableSortedBy, setTableSortedBy] = useState<SortColumn>('default');
  const [sortingDirection, setSortingDirection] = useState<SortDirection>('');
  const [hireDate, setHireDate] = useState<string | null>(null);
  const [yearsOfService, setYearsOfService] = useState<number | null>(null);
  const [vacationsPerYear, setVacationsPerYear] = useState(0);
  const [vacationsLeft, setVacationsLeft] = useState(0);
  const [completedDayOff, setCompletedDayOff] = useState(0);
  const [totalDayOff, setTotalDayOff] = useState(5);
  const [maximumSwitches] = useState(6);
  const [totalSwitchesDays, setTotalSwitchesDays] = useState(0);
  const [totalSwitchesOff, setTotalSwitchesOff] = useState(0);
  const [vacationsLeftNextPeriod, setVacationsLeftNextPeriod] = useState(0);
  const [vacationsPerYearNextPeriod, setVacationsPerYearNextPeriod] = useState(0);
  const [ptoDaysLeft, setPtoDaysLeft] = useState<PTODaysLeft>({} as PTODaysLeft);
  const [completingSwitchRequest, setCompletingSwitchRequest] = useState(false);
  const [isSaveRequestDisabled, setIsSaveRequestDisabled] = useState(false);
  const [toleranceDays, setToleranceDays] = useState(0);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const isPTOApprover = checkPTOApproverPermissions();
  const isHRApprover = checkHRApproverPermissions();

  // Refs for notification to avoid jQuery
  const notifTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Notification helper (replaces jQuery fadeIn/fadeOut) ──────────────────
  const showNotification = useCallback((message: string) =>
  {
    let el = document.getElementById('request-notification');
    if (!el)
    {
      el = document.createElement('div');
      el.id = 'request-notification';
      el.className = 'clipboard-notification ng-scope';
      Object.assign(el.style, {
        position: 'fixed',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: '99999',
        display: 'none',
      });
      document.body.prepend(el);
    }
    el.textContent = message;
    el.style.display = 'block';
    el.style.opacity = '1';
    if (notifTimerRef.current) clearTimeout(notifTimerRef.current);
    notifTimerRef.current = setTimeout(() =>
    {
      if (el) el.style.display = 'none';
    }, 6000);
  }, []);

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  useEffect(() =>
  {
    document.title = 'Corgee';
  }, []);

  useEffect(() =>
  {
    // Load holidays before fillTable so working-day requests deduct them
    getHolidays().then(() => getPersonInfo());
    getPTOPermissions();
    getRequestToleranceDays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Data fetching ─────────────────────────────────────────────────────────

  const getRequestToleranceDays = useCallback(() =>
  {
    VacationsService.GetRequestToleranceDays().then((data: number) =>
    {
      setToleranceDays(data);
    });
  }, []);

  const getHolidays = useCallback(() =>
  {
    return VacationsService.GetHolidays().then((data: Holiday[]) =>
    {
      itemsHolidaysRef.current = data;
      setItemsHolidays(data);
      return data;
    });
  }, []);

  const toStringDate = (date: Date | null): string =>
  {
    return date ? moment(date).format('YYYY-MM-DD') : '';
  };

  const getPTOPermissions = useCallback(() =>
  {
    VacationsService.GetPTOPermissions().then((data: Permission[]) =>
    {
      const newTypes: RequestTypeOption[] = [{ value: 0, label: 'Vacations', id: null }];

      data.forEach(p => newTypes.push({ value: 2, label: p.Name, id: p.Id }));

      if (isPTOApprover || isHRApprover)
      {
        newTypes.push({
          value: 20,
          label: 'Switch - Work Day',
          id: null,
          Message:
            'Warning: Only HR and Leads can request this. 6 days available per year which renew on December 31st/January 1st',
        });
        newTypes.push({
          value: 21,
          label: 'Switch - Day Off',
          id: null,
          Message:
            'Warning: Only HR and Leads can request this. 6 days available per year which renew on December 31st/January 1st',
        });
      }

      setItemsPermissions(data);
      setItemsRequestTypes(newTypes);
    });
  }, [isPTOApprover, isHRApprover]);

  const fillTable = useCallback(
    (
      currentPersonId: number,
      currentHireDate: string | null,
      currentRenewalDate: string | null,
      _currentNextRenewalDate: string | null,
      currentVacationsPerYear: number,
      currentItemsAssigned: Person[],
      currentItemsHolidays: Holiday[],
      currentYearsOfService: number | null,
    ) =>
    {
      setIsLoadingRequests(true);

      const vacationsModelById = {
        PersonId: currentPersonId,
        PersonIds: currentPersonId ? currentPersonId.toString() : '',
        DateFrom: selectedDateFrom ? selectedDateFrom.slice(0, 10) : '1970-01-01',
        DateTo: selectedDateTo ? selectedDateTo.slice(0, 10) : '1970-01-01',
        GetAll: true,
      };

      VacationsService.GetVacationRequestByIds(vacationsModelById).then((data: any[]) =>
      {
        const dataTableFillTotal: VacationTableItem[] = [];

        for (const row of data)
        {
          const dateFrom = new Date(row.DateFrom.split('T')[0]);
          const dateTo = new Date(row.DateTo.split('T')[0]);
          let days = calcDaysBetweenDates(dateFrom.getTime(), dateTo.getTime()) + 1;

          let lead = currentItemsAssigned.find(x => x.PersonID === row.LeadId);
          if (!lead && row.LeadId) lead = { PersonID: 1, Name: 'Admin' };

          if (row.IsWorkingDay)
          {
            const cur = new Date(dateFrom);
            while (cur <= dateTo)
            {
              const isWeekend = cur.getUTCDay() === 0 || cur.getUTCDay() === 6;
              const isHoliday = currentItemsHolidays.some(
                h => h.Date.split('T')[0] === cur.toISOString().split('T')[0],
              );
              if (isWeekend || isHoliday) days--;
              cur.setDate(cur.getDate() + 1);
            }
          }

          if (!lead) continue;

          const isSwitch = row.RequestTypeId === 1;
          const dateFromFmt = strDateToMMDDYYYY(row.DateFrom.split('T')[0]);
          const dateToFmt = strDateToMMDDYYYY(row.DateTo.split('T')[0]);
          const effectiveDays = days === 0 && row.RequestTypeName === 'Day Off' ? 1 : days;

          const checkEnabled = (startDate: string): boolean =>
            new Date(startDate).getTime() > parseDate(today).getTime();

          const checkAllowedCurrentDay = (typeName: string, start: string): boolean =>
            (ALLOWED_CURRENT_DAY_PERMISSIONS as readonly string[]).includes(typeName) &&
            new Date(start).getTime() >= parseDate(today).getTime();

          dataTableFillTotal.push({
            creationDate: strDateToMMDDYYYY(row.CreatedAt.split('T')[0]),
            LeadId: lead.PersonID,
            LeadName: lead.Name,
            requestTypeName: row.RequestTypeName,
            requestTypeId: row.RequestTypeId,
            days: effectiveDays,
            dateFrom: dateFromFmt,
            dateTo: dateToFmt,
            workedOn: row.WorkedOn ? strDateToMMDDYYYY(row.WorkedOn.split('T')[0]) : '',
            isWorkingDay: row.IsWorkingDay,
            status: row.IsActive ? getRequestStatus(row.Status) : 'Cancelled',
            requesterComments: row.Comments,
            approverComments: row.ApproverComments ?? '',
            requestId: row.VacationRequestId,
            enabled:
              isSwitch && getRequestStatus(row.Status) === 'Requested' && row.IsActive
                ? true
                : checkAllowedCurrentDay(row.RequestTypeName, dateFromFmt) && row.IsActive
                  ? true
                  : row.IsActive
                    ? checkEnabled(dateFromFmt)
                    : false,
          });
        }

        const sorted = [...dataTableFillTotal].sort(
          (a, b) => new Date(b.dateFrom).getTime() - new Date(a.dateFrom).getTime(),
        );

        const approved = sorted.filter(
          x => x.status === 'Approved' || x.status === 'Requested' || x.requestTypeName === 'Switch',
        );
        calcPTODaysLeft(
          approved,
          currentHireDate,
          currentRenewalDate,
          currentVacationsPerYear,
          currentItemsHolidays,
          currentYearsOfService,
        );

        setItemsFillTable(sorted);
        setPrevItemsFillTable(sorted);
        setOriginalItems(sorted);
        setTableSortedBy('default');
        setSortingDirection('');
        setIsLoadingRequests(false);
      });
    },
    [selectedDateFrom, selectedDateTo, today] // eslint-disable-line
  );

  const calcPTODaysLeft = useCallback(
    (
      approvedItems: VacationTableItem[],
      currentHireDate: string | null,
      currentRenewalDate: string | null,
      currentVacationsPerYear: number,
      _currentItemsHolidays: Holiday[],
      currentYearsOfService: number | null,
    ) =>
    {
      if (!currentHireDate) return;

      // Period boundaries are compared as local dates. `new Date('YYYY-MM-DD')` parses as
      // UTC midnight while `new Date('MM/DD/YYYY')` parses as local midnight, so mixing
      // them shifts the window by a day.
      const renewalStart = currentRenewalDate ? parseDate(currentRenewalDate) : null;

      const calcBeforePeriod = (): number =>
      {
        if (currentYearsOfService != null && currentYearsOfService > 1 && renewalStart)
        {
          const renewalBeforeDate = new Date(
            renewalStart.getFullYear() - 1,
            renewalStart.getMonth(),
            renewalStart.getDate(),
          );

          const askedCountBefore = approvedItems
            .filter(
              x =>
                x.requestTypeName === 'Vacations' &&
                (x.status === 'Approved' || x.status === 'Requested') &&
                parseMDYDate(x.creationDate) >= renewalBeforeDate &&
                parseMDYDate(x.creationDate) < renewalStart,
            )
            .reduce((s, x) => s + x.days, 0);

          const vacPrevYear = calcVacationsPerYearOffset(
            currentHireDate,
            currentYearsOfService,
            0,
          );
          if (askedCountBefore > vacPrevYear) return askedCountBefore - vacPrevYear;
        }
        return 0;
      };

      const askedBeforeVacations = calcBeforePeriod();

      const askedVacationsOnPeriod = approvedItems
        .filter(
          x =>
            x.requestTypeName === 'Vacations' &&
            renewalStart &&
            parseMDYDate(x.creationDate) >= renewalStart,
        )
        .reduce((acc, x) => acc + x.days, 0);

      const askedVacations = askedVacationsOnPeriod + askedBeforeVacations;

      const startOfYear = new Date(new Date().getFullYear(), 0, 1);

      const completedSw = approvedItems.filter(
        x =>
          x.requestTypeName === 'Switch' &&
          (x.status === 'Approved' || x.status === 'Requested') &&
          new Date(x.creationDate) >= startOfYear &&
          x.workedOn !== x.dateFrom,
      ).length;

      const totalSwitchesDaysVal =
        approvedItems.filter(
          x => x.requestTypeName === 'Switch - Work Day' && new Date(x.creationDate) >= startOfYear,
        ).length + completedSw;

      const totalSwitchesOffVal =
        approvedItems.filter(
          x => x.requestTypeName === 'Switch - Day Off' && new Date(x.creationDate) >= startOfYear,
        ).length + completedSw;

      const completedDo = approvedItems.reduce(
        (sum, x) =>
          x.requestTypeName === 'Day Off' && new Date(x.creationDate) >= startOfYear
            ? sum + x.days
            : sum,
        0,
      );

      const findPerm = (name: string) => itemsPermissions.find(x => x.Name === name);

      const permMaternity = findPerm('Maternity Leave');
      const permBereavement = findPerm('Bereavement Leave');
      const permAbsence = findPerm('Absence');
      const permSickLeave = findPerm('Sick Leave');
      const permPaternity = findPerm('Paternity Leave');
      const permBirthday = findPerm('Birthday Half-Day Off') ?? findPerm('Birthday Half-Day off');
      const permSpecialSwitch = findPerm('Special Switch');

      const countOf = (name: string) =>
        approvedItems.filter(x => x.requestTypeName === name).length;

      const perm6 = itemsPermissions.find(x => x.Id === 6);
      const defaultDayOff = perm6 ? perm6.Days : 5;
      const totalDo =
        selectedPermission?.Name === 'Day Off' ? selectedPermission.Days : defaultDayOff;

      let vacationsLeftNextPeriodVal = 0;
      let vacationsPerYearNextPeriodVal = 0;

      if (currentVacationsPerYear - askedVacations < 1 && currentYearsOfService != null)
      {
        vacationsPerYearNextPeriodVal = calcVacationsPerYearOffset(
          currentHireDate,
          currentYearsOfService,
          2,
        );
        vacationsLeftNextPeriodVal =
          vacationsPerYearNextPeriodVal -
          askedBeforeVacations -
          (askedVacationsOnPeriod - currentVacationsPerYear);
      }

      setPtoDaysLeft({
        maternityLeaveLeft: countOf('Maternity Leave') > 0 ? 0 : (permMaternity?.Days ?? 84),
        bereavementLeaveLeft: countOf('Bereavement Leave') > 0 ? 0 : (permBereavement?.Days ?? 5),
        absenceLeft: permAbsence ? permAbsence.Days - countOf('Absence') : 3,
        sickLeaveLeft: permSickLeave ? permSickLeave.Days - countOf('Sick Leave') : 3,
        paternityLeaveLeft: countOf('Paternity Leave') > 0 ? 0 : (permPaternity?.Days ?? 5),
        birthdayHalfLeaveLeft:
          countOf('Birthday Half-Day Off') + countOf('Birthday Half-Day off') > 0
            ? 0
            : (permBirthday?.Days ?? 1),
        specialSwitchLeaveLeft: countOf('Special Switch') > 0 ? 0 : (permSpecialSwitch?.Days ?? 1),
      });

      setVacationsLeft(
        currentVacationsPerYear - askedVacations < 1 ? 0 : currentVacationsPerYear - askedVacations,
      );
      setTotalSwitchesDays(totalSwitchesDaysVal);
      setTotalSwitchesOff(totalSwitchesOffVal);
      setCompletedDayOff(Math.max(0, totalDo - completedDo));
      setTotalDayOff(totalDo);
      setVacationsLeftNextPeriod(vacationsLeftNextPeriodVal);
      setVacationsPerYearNextPeriod(vacationsPerYearNextPeriodVal);
    },
    [itemsPermissions, selectedPermission],
  );

  const getPersonInfo = useCallback(
    (currentPersonId?: number) =>
    {
      const id = currentPersonId ?? personId;
      if (!id)
      {
        setPersonDetails(null);
        setHireDate(null);
        setYearsOfService(null);
        setRenewalDate(null);
        setNextRenewalDate(null);
        setVacationsLeft(0);
        setVacationsPerYear(0);
        setCompletedDayOff(0);
        setIsLoadingRequests(false);
        return;
      }

      VacationsService.GetPersonDetails(id.toString()).then(((data: PersonDetails) =>
      {
        setPersonDetails(data);
        getAllAsignedTo(id, data);
      }) as any);
    },
    [personId] // eslint-disable-line
  );

  const getHireDate = useCallback(
    (
      currentPersonId: number,
      _currentPersonDetails: PersonDetails,
      currentItemsAssigned: Person[],
      currentItemsHolidays: Holiday[],
    ) =>
    {
      VacationsService.GetHireDate(currentPersonId).then((date: string) =>
      {
        const hire = date === '' ? null : date;

        if (!hire)
        {
          setHireDate(null);
          setYearsOfService(null);
          setRenewalDate(null);
          setNextRenewalDate(null);
          setVacationsLeft(0);
          setVacationsPerYear(0);
          setCompletedDayOff(0);
          setIsLoadingRequests(false);
          return;
        }

        const now = new Date();
        const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const hireLocal = parseApiDate(hire);
        const years = calcYearsOfService(hire, todayLocal);

        // Renewal is the last anniversary already reached (the anniversary day itself counts).
        const renewal = new Date(
          hireLocal.getFullYear() + years,
          hireLocal.getMonth(),
          hireLocal.getDate(),
        );
        const nextRenewal = new Date(
          renewal.getFullYear() + 1,
          renewal.getMonth(),
          renewal.getDate(),
        );

        const renewalStr = toLocalISODate(renewal);
        const nextRenewalStr = toLocalISODate(nextRenewal);

        const vpy = calcVacationsPerYear(hire, years);

        setHireDate(hire);
        setYearsOfService(years);
        setRenewalDate(renewalStr);
        setNextRenewalDate(nextRenewalStr);
        setVacationsPerYear(vpy);

        fillTable(
          currentPersonId,
          hire,
          renewalStr,
          nextRenewalStr,
          vpy,
          currentItemsAssigned,
          currentItemsHolidays,
          years,
        );
      });
    },
    [fillTable],
  );

  const getAllAsignedTo = useCallback(
    (currentPersonId: number, currentPersonDetails: PersonDetails) =>
    {
      const getRequest = {
        Teams: '',
        GetTeamId: true,
        GetDepartmentId: true,
        GetPositionId: true,
        GetAllPeople: true,
      };

      VacationsService.GetAllAsignedTo(getRequest).then(((data: Person[]) =>
      {
        const currentUser = data.find(x => x.PersonID === currentPersonId);

        if (currentUser)
        {
          currentPersonDetails.TeamId = currentUser.TeamId;
          currentPersonDetails.Team = currentUser.Team;
          setPersonDetails({ ...currentPersonDetails });
          setItemsAssignedOnTeam(
            isHRApprover ? data : currentUser.TeamId ? data.filter(x => x.TeamId === currentUser.TeamId) : [currentUser],
          );
          setSelectedCurrentPerson(currentUser);
        }

        const assigned = data.filter(x => x.PersonID !== currentPersonId && x.PTOApprover === true);
        setItemsAssigned(assigned);
        setSelectedAssigned(null);

        getHireDate(currentPersonId, currentPersonDetails, assigned, itemsHolidaysRef.current);
      }) as any);
    },
    [isHRApprover, getHireDate],
  );

  // ── Sorting ───────────────────────────────────────────────────────────────

  const handleSortTableBy = useCallback(
    (columnName: SortColumn) =>
    {
      if (!columnName || columnName === 'default')
      {
        setTableSortedBy('default');
        setSortingDirection('');
        setItemsFillTable(originalItems);
        return;
      }

      let newDir: SortDirection;
      if (tableSortedBy === columnName && sortingDirection === 'asc')
      {
        newDir = 'desc';
      }
      else if (tableSortedBy === columnName && sortingDirection === 'desc')
      {
        setTableSortedBy('default');
        setSortingDirection('');
        setItemsFillTable(originalItems);
        return;
      }
      else
      {
        newDir = 'asc';
      }

      const sorted = sortTableItems(prevItemsFillTable, columnName, newDir, originalItems);
      setTableSortedBy(columnName);
      setSortingDirection(newDir);
      setItemsFillTable(sorted);
    },
    [tableSortedBy, sortingDirection, prevItemsFillTable, originalItems],
  );

  // ── Event handlers ────────────────────────────────────────────────────────

  const setWarningDaysFromToday = (dateFrom?: string, dateTo?: string): number =>
  {
    if (!selectedRequestType) return 0;
    const from = dateFrom ?? selectedDateFrom;
    const to = dateTo ?? selectedDateTo;
    const days = calcDaysBetweenDates(new Date(), parseDate(from));
    if (days < 15 && !isPTOApprover && !isHRApprover)
    {
      if (selectedRequestType.label === 'Vacations' || selectedRequestType.label === 'Day Off')
      {
        setVacationsWarning(
          'Warning: Your request should be made at least 15 days in advance. You can not request a day before today. This may result in rejection by HR or your Lead.',
        );
        setIsSaveRequestDisabled(true);
      }
      else
      {
        setVacationsWarning(null);
        setIsSaveRequestDisabled(false);
      }
      return days;
    }
    else
    {
      setIsSaveRequestDisabled(false);
    }

    const rawDays = calcDaysBetweenDates(
      from ? parseDate(from).getTime() : 0,
      to ? parseDate(to).getTime() : 0,
    ) + 1;
    const curDate = parseDate(from);
    let countedDays = 0;
    for (let i = 0; i < rawDays; i++)
    {
      const isWeekend = curDate.getUTCDay() === 0 || curDate.getUTCDay() === 6;
      const isHoliday = itemsHolidays.some(
        h => h.Date.split('T')[0] === curDate.toISOString().split('T')[0],
      );
      if (!isWeekend && !isHoliday) countedDays++;
      if (countedDays < rawDays) curDate.setUTCDate(curDate.getDate() + 1);
    }

    if (
      (vacationsLeftNextPeriod > 0 || vacationsLeft - countedDays < 0) &&
      selectedRequestType.label === 'Vacations'
    )
    {
      setVacationsWarning('Warning: You will ask vacations of the next period.');
    }
    else
    {
      setVacationsWarning(null);
    }

    return days;
  };

  const onSelectRequestTypeChange = (item: SingleValue<RequestTypeOption>) =>
  {
    let perm: Permission | null = null;

    if (item?.id)
    {
      perm = itemsPermissions.find(x => x.Id === item.id) ?? null;
      if (perm)
      {
        setAvailableEndDate(perm.AvailableEndDate ?? true);
        setSelectedPermission(perm);
      }
    }
    else
    {
      setSelectedPermission(null);
      if (item?.value === 20 || item?.value === 21)
      {
        setAvailableEndDate(false);
      }
      else
      {
        setAvailableEndDate(true);
      }
    }

    setSelectedRequestType(item as RequestTypeOption | null);
    setInvalidRequestType(!item);

    if (perm?.Message || (item as RequestTypeOption)?.Message)
    {
      setVacationsWarning(perm?.Message ?? (item as RequestTypeOption)?.Message ?? null);
    }
    else if (selectedDateFrom)
    {
      setWarningDaysFromToday(selectedDateFrom);
    }
  };

  const onSelectAssignedChange = useCallback((item: SingleValue<Person>) =>
  {
    setSelectedAssigned(item as Person | null);
    setInvalidAssigned(!item);
  }, []);

  const onSelectCurrentPersonChange = (item: SingleValue<Person>) =>
  {
    const newPersonId = item ? (item as Person).PersonID : null;
    setSelectedCurrentPerson(item as Person | null);
    setPersonId(newPersonId ?? 0);
    setItemsFillTable([]);
    setSelectedAssigned(null);
    setIsLoadingRequests(true);

    if (newPersonId)
    {
      VacationsService.GetPersonDetails(newPersonId.toString()).then(((data: PersonDetails) =>
      {
        setPersonDetails(data);
        getAllAsignedTo(newPersonId, data);
      }) as any);
    }
    else
    {
      getPersonInfo(0);
    }
  };

  const validateBirthdayLeft = (requestedDateFrom: string): boolean =>
  {
    const askedCountBirthdays = itemsFillTable
      .filter(
        x =>
          (x.requestTypeName === 'Birthday Half-Day Off' ||
            x.requestTypeName === 'Birthday Half-Day off') &&
          (x.status === 'Approved' || x.status === 'Requested') &&
          new Date(x.dateFrom).getFullYear() === new Date(requestedDateFrom).getFullYear(),
      )
      .reduce((a, x) => a + x.days, 0);
    return askedCountBirthdays > 0;
  };

  const validateDaysLeftUnAvailable = (
    reqType: RequestTypeOption,
    pto: PTODaysLeft & {
      completedDayOff: number;
      totalSwitchesDays: number;
      totalSwitchesOff: number;
      vacationsLeft: number;
      vacationsLeftNextPeriod: number;
    },
    dateFrom: string,
  ): boolean =>
  {
    const warn = (msg: string) =>
    {
      setVacationsWarning(msg);
      return true;
    };
    const { label } = reqType;

    if (label === 'Day Off' && pto.completedDayOff < 1)
      return warn("You don't have any Day Off available");
    if (label === 'Switch - Work Day' && pto.totalSwitchesDays >= maximumSwitches)
      return warn("You don't have any Switch work days available");
    if (label === 'Switch - Day Off' && pto.totalSwitchesOff >= maximumSwitches)
      return warn("You don't have any Switch days off available");
    if (label === 'Vacations' && pto.vacationsLeft < 1 && pto.vacationsLeftNextPeriod < 1)
      return warn("You don't have any Vacation available");
    if (label === 'Maternity Leave' && pto.maternityLeaveLeft < 1)
      return warn("You don't have any Maternity Leave available");
    if (label === 'Bereavement Leave' && pto.bereavementLeaveLeft < 1)
      return warn("You don't have any Bereavement Leave available");
    if (label === 'Absence' && pto.absenceLeft < 1) return false;
    if (label === 'Paternity Leave' && pto.paternityLeaveLeft < 1)
      return warn("You don't have any Paternity Leave available");
    if (label === 'Sick Leave' && pto.sickLeaveLeft < 1) return false;
    if (label === 'Birthday Half-Day Off' || label === 'Birthday Half-Day off')
    {
      if (validateBirthdayLeft(dateFrom))
        return warn("You don't have any Birthday Half-Day Off available");
      return false;
    }
    if (label === 'Special Switch' && pto.specialSwitchLeaveLeft < 1)
      return warn("You don't have any Special Switch available");
    return false;
  };

  const checkDuplicateDate = (dateFrom: string, dateTo: string): boolean =>
  {
    if (!selectedRequestType) return false;
    const isSwitch = selectedRequestType.value === 1;
    const checkFrom = isSwitch ? dateTo : dateFrom;
    const from1 = parseDate(checkFrom).getTime();
    const to1 = parseDate(dateTo).getTime();

    return itemsFillTable
      .filter(x => x.status === 'Approved' || x.status === 'Requested')
      .some(item =>
      {
        const from2 = new Date(item.dateFrom).getTime();
        const to2 = new Date(item.dateTo).getTime();
        return from1 <= to2 && from2 <= to1;
      });
  };

  const validateDates = useCallback(
    (dateFrom: string, dateTo: string): boolean =>
    {
      if (!selectedRequestType) return false;
      const isSwitch = selectedRequestType.value === 1;
      let valid = true;

      if (!dateFrom)
      {
        setSelectedDateFromMsg('You have not selected a Date.');
        valid = false;
      }
      else setSelectedDateFromMsg(null);

      if (!dateTo || dateTo === '')
      {
        if (!isSwitch)
        {
          setSelectedDateToMsg('You have not selected a Date.');
          valid = false;
        }
      }
      else setSelectedDateToMsg(null);

      if (!valid) return false;

      const hasSickPermit =
        (isPTOApprover || isHRApprover) && selectedRequestType.label === 'Sick Leave';
      let isValidFrom = false;
      let isValidTo = false;
      let isFromFarLater = false;
      let isToFarLater = false;

      const limitDate = () =>
      {
        const d = new Date(nextRenewalDate!);
        d.setDate(d.getDate() + toleranceDays);
        return d;
      };

      if (
        (isPTOApprover || isHRApprover) &&
        (personId !== originalPersonId || hasSickPermit) &&
        !isSwitch
      )
      {
        isValidFrom = true;
        isValidTo = parseDate(dateTo).getTime() >= parseDate(dateFrom).getTime();
        isFromFarLater = !(parseDate(dateFrom).getTime() <= limitDate().getTime());
        isToFarLater = !(parseDate(dateTo).getTime() <= limitDate().getTime());
      }
      else if (isSwitch)
      {
        isValidFrom = true;
        isValidTo =
          !dateTo || dateTo === dateFrom
            ? true
            : parseDate(dateTo).getTime() > parseDate(dateFrom).getTime() &&
              parseDate(dateTo).getFullYear() === new Date().getFullYear();
      }
      else if (
        (ALLOWED_CURRENT_DAY_PERMISSIONS as readonly string[]).includes(selectedRequestType.label)
      )
      {
        isValidFrom =
          parseDate(dateFrom).getTime() >= parseDate(today).getTime() &&
          parseDate(dateFrom).getFullYear() === new Date().getFullYear();
        isValidTo = parseDate(dateTo).getTime() >= parseDate(dateFrom).getTime();
      }
      else
      {
        isValidFrom = parseDate(dateFrom).getTime() > parseDate(today).getTime();
        isFromFarLater = !(parseDate(dateFrom).getTime() <= limitDate().getTime());
        isValidTo = parseDate(dateTo).getTime() >= parseDate(dateFrom).getTime();
        isToFarLater = !(parseDate(dateTo).getTime() <= limitDate().getTime());
      }

      const isAllowed = (ALLOWED_CURRENT_DAY_PERMISSIONS as readonly string[]).includes(
        selectedRequestType.label,
      );

      setSelectedDateFromMsg(
        isFromFarLater
          ? `The start date cannot be more than ${toleranceDays} days after end period`
          : isValidFrom
            ? null
            : isAllowed
              ? 'The start date cannot be older than the current day'
              : 'The start date cannot be the current date or older',
      );

      setSelectedDateToMsg(
        isToFarLater
          ? `The end date cannot be more than ${toleranceDays} days after end period`
          : isValidTo
            ? null
            : `The end date cannot be older ${isSwitch ? 'or equal to' : 'than'} the start date or outside year limit`,
      );

      return isValidFrom && isValidTo && !isFromFarLater && !isToFarLater;
    },
    [
      selectedRequestType,
      isPTOApprover,
      isHRApprover,
      personId,
      originalPersonId,
      nextRenewalDate,
      toleranceDays,
      today,
    ],
  );

  const checkFirstVacation = (): boolean =>
  {
    const countVacationsAsked = itemsFillTable
      .filter(
        x =>
          x.requestTypeName === 'Vacations' &&
          (x.status === 'Approved' || x.status === 'Requested') &&
          renewalDate != null &&
          new Date(x.creationDate) >= new Date(renewalDate) &&
          new Date(x.dateFrom) >= new Date(renewalDate),
      )
      .reduce((a, x) => a + x.days, 0);
    return countVacationsAsked === 0;
  };

  const saveRequest = () =>
  {
    if (!hireDate)
    {
      setVacationsWarning(
        'Warning: Your request can not be processed until you have a "Hire Date" set in your information. Contact Human Resources.',
      );
      return;
    }

    if (!selectedRequestType || !selectedAssigned)
    {
      if (!selectedRequestType) setInvalidRequestType(true);
      if (!selectedAssigned) setInvalidAssigned(true);
      return;
    }

    const fullPTO = {
      completedDayOff,
      totalSwitchesDays,
      totalSwitchesOff,
      vacationsLeft,
      vacationsLeftNextPeriod,
      ...ptoDaysLeft,
    };
    if (validateDaysLeftUnAvailable(selectedRequestType, fullPTO, selectedDateFrom)) return;

    const dateFromDate = new Date(selectedDateFrom);
    const isRestrictedType = [
      'Switch',
      'Switch - Work Day',
      'Switch - Day Off',
      'Vacations',
    ].includes(selectedRequestType.label);
    const hasPermRestriction = selectedPermission && !selectedPermission.CanSelectPastDates;

    if (!isAdminInit && !isPTOApprover && !isHRApprover && dateFromDate < new Date() && (isRestrictedType || hasPermRestriction))
    {
      setVacationsWarning("You can't select past dates for this request.");
      return;
    }

    setInvalidAssigned(false);
    setInvalidRequestType(false);

    const isSwitchWork = selectedRequestType.value === 20;
    const isSwitchOff = selectedRequestType.value === 21;
    const isDayOff = selectedRequestType.label === 'Day Off';
    let newDateTo = selectedDateTo;

    if (selectedPermission && !selectedPermission.AvailableEndDate)
    {
      const cur = parseDate(selectedDateFrom);
      if (selectedPermission.IsWorkingDay)
      {
        let counted = 0;
        while (counted <= selectedPermission.Days - 1)
        {
          const isWeekend = cur.getUTCDay() === 0 || cur.getUTCDay() === 6;
          const isHoliday = itemsHolidays.some(
            h => h.Date.split('T')[0] === cur.toISOString().split('T')[0],
          );
          if (!isWeekend && !isHoliday) counted++;
          if (counted < selectedPermission.Days) cur.setUTCDate(cur.getDate() + 1);
        }
      }
      else
      {
        cur.setDate(cur.getDate() + (selectedPermission.Days - 1));
      }
      newDateTo = cur.toISOString().split('T')[0]!;
    }

    let requestedDays = 0;
    if (selectedRequestType.label === 'Vacations' || isSwitchWork || isSwitchOff || isDayOff)
    {
      const daysFromToday = setWarningDaysFromToday(selectedDateFrom);
      if (daysFromToday < 15 && !isPTOApprover && !isHRApprover) return;

      requestedDays =
        calcDaysBetweenDates(
          parseDate(selectedDateFrom).getTime(),
          parseDate(newDateTo).getTime(),
        ) + 1;

      if (selectedRequestType.label === 'Vacations')
      {
        const cur = parseDate(selectedDateFrom);
        let counted = 0;
        for (let i = 0; i < requestedDays; i++)
        {
          const isWeekend = cur.getUTCDay() === 0 || cur.getUTCDay() === 6;
          const isHoliday = itemsHolidays.some(
            h => h.Date.split('T')[0] === cur.toISOString().split('T')[0],
          );
          if (!isWeekend && !isHoliday) counted++;
          if (counted < requestedDays) cur.setUTCDate(cur.getDate() + 1);
        }
        requestedDays = counted;

        if (requestedDays > vacationsLeft)
        {
          const renewalStart = renewalDate ? parseDate(renewalDate) : null;
          const sumNextYear =
            calcVacationsPerYearOffset(hireDate!, yearsOfService ?? 0, 2) + vacationsPerYear;
          let totalAsked = itemsFillTable
            .filter(
              x =>
                x.requestTypeName === 'Vacations' &&
                (x.status === 'Approved' || x.status === 'Requested') &&
                renewalStart &&
                parseMDYDate(x.dateFrom) >= renewalStart,
            )
            .reduce((s, x) => s + x.days, 0);
          if (yearsOfService != null && yearsOfService > 1 && renewalStart)
          {
            const renewalBeforeDate = new Date(
              renewalStart.getFullYear() - 1,
              renewalStart.getMonth(),
              renewalStart.getDate(),
            );
            const askedBefore = itemsFillTable
              .filter(
                x =>
                  x.requestTypeName === 'Vacations' &&
                  (x.status === 'Approved' || x.status === 'Requested') &&
                  parseMDYDate(x.creationDate) >= renewalBeforeDate &&
                  parseMDYDate(x.creationDate) < renewalStart,
              )
              .reduce((s, x) => s + x.days, 0);
            const vacPrevYear = calcVacationsPerYearOffset(hireDate!, yearsOfService, 0);
            if (askedBefore > vacPrevYear) totalAsked += askedBefore - vacPrevYear;
          }
          if (sumNextYear - totalAsked < 1)
          {
            setVacationsWarning("Warning:  You don't have available vacations to request");
            return;
          }
        }
        if (requestedDays === 0)
        {
          setVacationsWarning("Warning:  You can't request these days");
          return;
        }
      }
      else if (isSwitchWork && totalSwitchesDays >= maximumSwitches)
      {
        setVacationsWarning("Warning:  You don't have available switches work day to request");
        return;
      }
      else if (isSwitchOff && totalSwitchesOff >= maximumSwitches)
      {
        setVacationsWarning("Warning:  You don't have available switches day off to request");
        return;
      }
    }

    if (isSwitchWork || isSwitchOff || isDayOff) newDateTo = selectedDateFrom;

    if (!validateDates(selectedDateFrom, newDateTo)) return;

    if (checkDuplicateDate(selectedDateFrom, newDateTo))
    {
      showNotification('This date is already requested');
      setIsSaveRequestDisabled(false);
      return;
    }

    const isWorkingDay =
      !!selectedPermission?.IsWorkingDay || selectedRequestType.label === 'Vacations';
    const isFirstVacation = checkFirstVacation();

    const vacationsModel = {
      RequestTypeId: selectedRequestType.value,
      RequestTypeName: selectedPermission ? selectedPermission.Name : selectedRequestType.label,
      PersonId: personId,
      LeadId: selectedAssigned.PersonID,
      DateFrom: selectedDateFrom,
      DateTo: newDateTo,
      WorkedOn: null,
      isWorkingDay,
      Comments: commentsValue,
      SocketConnectionID: useSocketConnectionId,
      NotifyApprover: true,
      SendEmailFirstVacation: selectedRequestType.label === 'Vacations' ? isFirstVacation : false,
      Days: requestedDays,
      Period: `${renewalDate} - ${nextRenewalDate}`,
      VacationsPerPeriod: vacationsPerYear,
    };

    if (vacationsModel.RequestTypeName === 'Day Off')
    {
      vacationsModel.DateTo = new Date(selectedDateTo).toISOString().split('T')[0]!;
    }

    setIsLoadingRequests(true);
    setVacationsWarning(null);
    setAvailableEndDate(true);

    VacationsService.CreateVacationsRequest(vacationsModel).then((data: number) =>
    {
      if (data > 0)
      {
        showNotification('Request was sent successfully');
        const next = new Date();
        next.setDate(next.getDate() + 1);
        setSelectedAssigned(null);
        setSelectedRequestType(null);
        setCommentsValue('');
        setSelectedDateFrom(next.toISOString().split('T')[0]!);
        setSelectedDateTo(next.toISOString().split('T')[0]!);
      }
      else
      {
        showNotification('Error sending Request');
      }

      fillTable(
        personId,
        hireDate,
        renewalDate,
        nextRenewalDate,
        vacationsPerYear,
        itemsAssigned,
        itemsHolidays,
        yearsOfService,
      );
    });
  };

  const showConfirmationModal = useCallback(
    (item: VacationTableItem, option: 'edit' | 'delete') =>
    {
      setSelectedRequestItem(item);
      setSelectedDateSwitchOff(getDateWithAddedDay(item.dateFrom));
      setCompletingSwitchRequest(option === 'edit');
      setShowModal(true);
    },
    [],
  );

  const closeModal = useCallback(() =>
  {
    setSelectedRequestItem(null);
    setShowModal(false);
    setSelectedDateSwitchOff(new Date().toISOString().split('T')[0]!);
    setSwitchOffWarning(null);
  }, []);

  const deleteVacationRequest = useCallback(() =>
  {
    if (!selectedRequestItem) return;
    setLoadingRequest(true);
    VacationsService.DeleteVacationRequest({
      VacationRequestId: selectedRequestItem.requestId,
      NotifyToId: selectedRequestItem.LeadId,
      StatusValue: 3,
      SocketConnectionID: useSocketConnectionId,
    }).then(() =>
    {
      closeModal();
      setLoadingRequest(false);
      fillTable(
        personId,
        hireDate,
        renewalDate,
        nextRenewalDate,
        vacationsPerYear,
        itemsAssigned,
        itemsHolidays,
        yearsOfService,
      );
    });
  }, [selectedRequestItem, closeModal, fillTable, personId, hireDate, renewalDate, nextRenewalDate, vacationsPerYear, itemsAssigned, itemsHolidays]); // eslint-disable-line

  const updateSwitchRequest = useCallback(() =>
  {
    if (!selectedDateSwitchOff || !selectedRequestItem)
    {
      setSwitchOffWarning('Warning: You have to select a valid date.');
      return;
    }
    setLoadingRequest(true);
    VacationsService.UpdateSwitchRequest({
      VacationRequestId: selectedRequestItem.requestId,
      NotifyToId: selectedRequestItem.LeadId,
      StatusValue: 0,
      SocketConnectionID: useSocketConnectionId,
      selectedDate: selectedDateSwitchOff,
    }).then(() =>
    {
      closeModal();
      setLoadingRequest(false);
      fillTable(
        personId,
        hireDate,
        renewalDate,
        nextRenewalDate,
        vacationsPerYear,
        itemsAssigned,
        itemsHolidays,
        yearsOfService,
      );
    });
  }, [selectedDateSwitchOff, selectedRequestItem, closeModal, fillTable, personId, hireDate, renewalDate, nextRenewalDate, vacationsPerYear, itemsAssigned, itemsHolidays]); // eslint-disable-line

  // Shared report select style, same as the other reports
  const comboBoxSelectStyles = buildReportSelectStyles<any, false>(isDark);

  const sharedProps = {
    autoFocus: false,
    searchable: true,
    openOnFocus: true,
    autosize: false,
    closeOnSelect: false,
    onBlur: () => {},
    styles: comboBoxSelectStyles,
    components: { DropdownIndicator: () => null, IndicatorSeparator: () => null },
    closeMenuOnSelect: true,
    isClearable: true,
    isSearchable: true,
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="VacationsRequest" id="VacationsRequest">
      {/* ── Confirmation Modal ────────────────────────────────────────────── */}
      <Modal
        open={showModal}
        onClose={closeModal}
        className="w-[540px]"
        title={`${completingSwitchRequest ? 'Complete' : 'Delete'} PTO Request`}
        preventCloseClickOut
      >
        {completingSwitchRequest && selectedRequestItem ? (
          <div>
            <div className="py-[10px] mt-[10px]">
              <div className="delete-text">Please select the day off for this switch.</div>
            </div>
            <div className="switch-datepicker">
              <DatePicker
                selected={selectedDateSwitchOff ? moment(selectedDateSwitchOff, 'YYYY-MM-DD').toDate() : null}
                onChange={(d: Date | null) => setSelectedDateSwitchOff(d ? moment(d).format('YYYY-MM-DD') : '')}
                dateFormat="MM/dd/yyyy"
                customInput={<VrDateInput onClear={() => setSelectedDateSwitchOff('')} />}
                popperProps={{ strategy: 'fixed' }}
                showPopperArrow={false}
                calendarClassName="vr-datepicker-calendar"
              />
            </div>
            {switchOffWarning && (
              <div
                className="vacations-warning"
                style={{ marginInline: 10, textAlign: 'center' }}
              >
                {switchOffWarning}
              </div>
            )}
          </div>
        ) : (
          <div className="py-[10px] mt-[10px]">
            <div className="delete-text">Are you sure you want to delete this request?</div>
          </div>
        )}

        <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
          <button
            type="button"
            disabled={loadingRequest}
            onClick={completingSwitchRequest ? updateSwitchRequest : deleteVacationRequest}
            className="bg-corgee-blue hover:bg-corgee-BlueHover border text-white text-xs h-7 px-3 py-1"
          >
              Confirm
          </button>
          <button
            type="button"
            onClick={closeModal}
            className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1"
          >
              Cancel
          </button>
        </div>
      </Modal>

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="row  marginAlignLeft main-content">
        <div className="col-md-12">
          <div className="stime-header spaceRow">
            <h3>PTO Request</h3>
            <p className="info_AR">
                This report shows your available PTO days to request. The user(s) should assign a
                Start date and an End date, skipping the holidays and weekends.
              <br />
                They will receive the Approved/Rejected notification.
              <br />
              {isHRApprover
                ? 'As a HHRR Approver, you can select any member to make a request.'
                : isPTOApprover
                  ? 'As a PTO Approver, you can select any Team member to make a request.'
                  : null}
            </p>
          </div>
        </div>
      </div>

      {/* ── Form ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col">
        <div className="marginAlignLeft">
          {/* Request Type */}
          <div className="col-md-2 marginTopInputs">
            <div className="form-group">
              <div className="col-sm-12 input-group nid">
                <label className="stime-label pull-left">Request Type:</label>
                <Select
                  {...sharedProps}
                  options={itemsRequestTypes}
                  getOptionValue={opction => opction.label}
                  onChange={onSelectRequestTypeChange}
                  value={selectedRequestType}
                  isMulti={false}
                  styles={comboBoxSelectStyles}
                />
                {invalidRequestType && (
                  <div className="cw-validation-Note">
                    <div className="cw-validation-Message">
                        You have to specify your pto type.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Name */}
          <div className="col-md-2 marginTopInputs">
            <div className="form-group">
              <div className="col-sm-12 input-group nid">
                <label className="stime-label pull-left">Name:</label>
                { isPTOApprover || isHRApprover ? (
                  <Select
                    {...sharedProps}
                    options={itemsAssignedOnTeam}
                    onChange={onSelectCurrentPersonChange}
                    value={selectedCurrentPerson}
                    isMulti={false}
                    getOptionLabel={o => o.Name}
                    getOptionValue={o => String(o.PersonID)}
                    styles={comboBoxSelectStyles}
                  />
                ) : (
                  <Select
                    {...sharedProps}
                    placeholder={personDetails?.Name ?? ''}
                    isMulti={false}
                    styles={comboBoxSelectStyles}
                    options={[]}
                    isDisabled={true}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Lead */}
          <div className="col-md-2 marginTopInputs">
            <div className="form-group">
              <div className="col-sm-12 input-group nid">
                <label className="stime-label pull-left">Actual Lead:</label>
                <Select
                  {...sharedProps}
                  options={itemsAssigned}
                  onChange={onSelectAssignedChange}
                  value={selectedAssigned}
                  getOptionLabel={o => o.Name}
                  getOptionValue={o => String(o.PersonID)}
                  styles={comboBoxSelectStyles}
                />
                {invalidAssigned && (
                  <div className="cw-validation-Note">
                    <div className="cw-validation-Message">
                        You have to specify your actual team lead.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="spaceline-break" />

          {/* Date From */}
          <div className="col-md-2 marginTopInputs">
            <div className="form-group">
              <div className="col-sm-12 input-group nid">
                <label className="stime-label pull-left">
                  {selectedRequestType?.value === 1 ? 'Worked On' : 'From'}:
                </label>
                <DatePicker
                  selected={selectedDateFrom ? moment(selectedDateFrom, 'YYYY-MM-DD').toDate() : null}
                  onChange={(d: Date | null) =>
                  {
                    const dateStr = toStringDate(d);
                    setSelectedDateFrom(dateStr);
                    if (dateStr) setWarningDaysFromToday(dateStr);
                  }}
                  dateFormat="MM/dd/yyyy"
                  customInput={<VrDateInput onClear={() => setSelectedDateFrom('')} />}
                  popperProps={{ strategy: 'fixed' }}
                  showPopperArrow={false}
                  calendarClassName="vr-datepicker-calendar"
                />
                {selectedDateFromMsg && (
                  <div className="cw-validation-Note">
                    <div className="cw-validation-Message">{selectedDateFromMsg}</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Date To */}
          {availableEndDate && (
            <div className="col-md-2 marginTopInputs">
              <div className="form-group">
                <div className="col-sm-12 input-group nid">
                  <label className="stime-label pull-left">
                    {selectedRequestType?.value === 1 ? 'Off Date' : 'To'}:
                  </label>
                  <DatePicker
                    selected={selectedDateTo ? moment(selectedDateTo, 'YYYY-MM-DD').toDate() : null}
                    onChange={(d: Date | null) =>
                    {
                      const dateStr = toStringDate(d);
                      setSelectedDateTo(dateStr);
                      if (dateStr) setWarningDaysFromToday(undefined, dateStr);
                    }}
                    dateFormat="MM/dd/yyyy"
                    customInput={<VrDateInput onClear={() => setSelectedDateTo('')} />}
                    popperProps={{ strategy: 'fixed' }}
                    showPopperArrow={false}
                    calendarClassName="vr-datepicker-calendar"
                  />
                  {selectedDateToMsg && (
                    <div className="cw-validation-Note">
                      <div className="cw-validation-Message">{selectedDateToMsg}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Comments */}
          <div className="col-md-2 marginTopInputs">
            <div className="form-group">
              <div className="item-security-settings-container">
                <label className="stime-label pull-left">Comments:</label>
                <textarea
                  name="comments"
                  id="comments"
                  value={commentsValue}
                  onChange={e => setCommentsValue(e.target.value)}
                  cols={36}
                  rows={2}
                  maxLength={100}
                />
              </div>
            </div>
          </div>

          {/* Save button inline with the filters; hidden label reserves the label height */}
          <div className="col-md-2 marginTopInputs vr-save-inline">
            <label className="stime-label pull-left" aria-hidden="true" style={{ visibility: 'hidden' }}>
              &nbsp;
            </label>
            <button
              type="button"
              className={`custom2 update buttonAction btnChangeColorWithLayer ${isSaveRequestDisabled ? 'disabled' : ''}`}
              onClick={saveRequest}
            >
                Save Request
            </button>
          </div>
        </div>
        <div>
          {vacationsWarning && <div className="vacations-warning">{vacationsWarning}</div>}
        </div>
      </div>
      {/* ── Stats bar ─────────────────────────────────────────────────────── */}
      <div className="dispTable">
        <div className="marginCalcs">
          <div className="stime-bottom-div enable-help-tooltip" data-title="Vacation Days Left">
            <div className="stime-bottom-title">
              {vacationsLeft} / {vacationsPerYear}
            </div>
            <span className="stime-bottom-subtitle">Vacation Left</span>
          </div>
          {vacationsLeft === 0 && (
            <div
              className="stime-top-div enable-help-tooltip"
              data-title={`Your next period vacation left ${vacationsLeftNextPeriod} / ${vacationsPerYearNextPeriod}`}
            >
              <i
                className="fa fa-info-circle"
                style={{ display: 'inline-block', width: 20, color: '#999' }}
              />
            </div>
          )}
        </div>
        <div className="marginCalcs">
          <div className="stime-bottom-div enable-help-tooltip" data-title="Switch Days Left">
            <div className="stime-bottom-title">
              {totalSwitchesDays} / {maximumSwitches}
            </div>
            <span className="stime-bottom-subtitle">Switch Work Days</span>
          </div>
        </div>
        <div className="marginCalcs">
          <div className="stime-bottom-div enable-help-tooltip" data-title="Switch Days Left">
            <div className="stime-bottom-title">
              {totalSwitchesOff} / {maximumSwitches}
            </div>
            <span className="stime-bottom-subtitle">Switch Days Off</span>
          </div>
        </div>
        <div className="marginCalcs">
          <div className="stime-bottom-div enable-help-tooltip" data-title="Days Off Left">
            <div className="stime-bottom-title">
              {completedDayOff} / {totalDayOff}
            </div>
            <span className="stime-bottom-subtitle">Day Off Left</span>
          </div>
        </div>
        <div className="spaceCell" />
        {[
          { title: 'Entry Date', value: hireDate ?? '-', label: 'Entry Date' },
          {
            title: 'Labor Old',
            value: yearsOfService ?? '-',
            label: 'Labor Old',
          },
          {
            title: 'Start Period',
            value: renewalDate ? strDateToMMDDYYYY(renewalDate) : '-',
            label: 'Start Period',
          },
          {
            title: 'End Period',
            value: nextRenewalDate ? strDateToMMDDYYYY(nextRenewalDate) : '-',
            label: 'End Period',
          },
        ].map(({ title, value, label }) => (
          <div className="marginCalcs" key={label}>
            <div className="stime-bottom-div enable-help-tooltip" data-title={title}>
              <div className="stime-bottom-title">{value}</div>
              <span className="stime-bottom-subtitle">{label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      <div className="spaceRow dispTable">
        <div className="table-responsive wrapper table-wrapper">
          <table className="noTableBottom table table-sm table-bordered">
            <thead>
              <tr>
                {(
                    [
                      ['creationDate', 'Created On', 'creation Date'],
                      ['lead', 'Lead', 'Lead Name'],
                      ['requestType', 'Request Type', 'Request Type'],
                      ['days', 'Days', 'Days'],
                      ['workedOn', 'Worked On', 'Worked On'],
                      ['dateFrom', 'From', 'Date From'],
                      ['dateTo', 'To', 'Date To'],
                      ['status', 'Status', 'Status'],
                      ['requesterComments', 'Requester Comments', 'Requester Comments'],
                      ['approverComments', 'Approver Comments', 'Approver Comments'],
                    ] as [SortColumn, string, string][]
                ).map(([col, label, title]) => (
                  <SortableTh
                    key={col}
                    column={col}
                    label={label}
                    title={title}
                    sortedBy={tableSortedBy}
                    direction={sortingDirection}
                    onSort={handleSortTableBy}
                  />
                ))}
                <th className="enable-help-tooltip" data-title="Actions">
                    Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {!isLoadingRequests ? (
                itemsFillTable.length > 0 ? (
                  itemsFillTable.map((item, i) => (
                    <tr key={i}>
                      <td className="stime-td-report">{item.creationDate}</td>
                      <td className="stime-td-report">{item.LeadName}</td>
                      <td className="stime-td-report">{item.requestTypeName}</td>
                      <td className="stime-td-report">{item.days}</td>
                      <td className="stime-td-report">{item.workedOn}</td>
                      <td className="stime-td-report">
                        {item.requestTypeId === 1 &&
                          item.workedOn === item.dateFrom &&
                          item.enabled ? (
                            <button
                              className="corgee-button"
                              onClick={() => showConfirmationModal(item, 'edit')}
                            >
                              Edit
                            </button>
                          ) : (
                            item.dateFrom
                          )}
                      </td>
                      <td className="stime-td-report">
                        {item.requestTypeId === 1 &&
                          item.workedOn === item.dateFrom &&
                          item.enabled
                          ? '-'
                          : item.dateTo}
                      </td>
                      <td className="stime-td-report">{item.status}</td>
                      <td className="stime-td-report comments">{item.requesterComments}</td>
                      <td className="stime-td-report comments">{item.approverComments}</td>
                      <td className="stime-td-report">
                        {item.enabled ? (
                          <button
                            className="corgee-button delete"
                            onClick={() => showConfirmationModal(item, 'delete')}
                          >
                              Delete
                          </button>
                        ) : (
                          'Disabled'
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={11} style={{ border: 'none' }}>
                      <div className="row spaceRow" />
                      <div className="row spaceRow">
                        <div className="col-md-12">
                          <div className="stime-bottom-Results notFoundRecords">
                              No results found
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {/* Spinner */}
      <div
        className="text-center"
        style={{ position: 'absolute', marginLeft: 0, width: '85%', marginTop: 160 }}
      >
        <div
          className="spinner-border"
          role="status"
          style={{ display: isLoadingRequests ? 'inline-flex' : 'none' }}
        >
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    </div>
  );
};

export default VacationsRequestPage;
